import { useState, useEffect } from "react";
import { useRbac } from "../../context";
import {Table} from "../../components/table/table"
import { getAssignedJobsForHr, triggerAssignedJobsForAdmin } from "../../services/jobsapi";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ChevronLeft, ChevronRight } from "lucide-react";
import toast from "react-hot-toast";

// --- Types ---
interface AssignedJob {
  _id: string;
  candidateName: string;
  email: string;
  phone: string;
  appliedDate: string;
  status: string;
  [key: string]: any;
}

interface Pagination {
  page: number;
  limit: number;
  totalResults: number;
}

interface ApiResponse {
  data: AssignedJob[];
  metadata: { totalResults: number }[];
}

// 👇 The type expected by your API
interface AssignedJobQuery {
  search?: string;
  fromDate?: string;
  toDate?: string;
  page: string;
  limit: string;
}

// --- Component ---
export const AssignedJobs: React.FC = () => {
  const [assigned, setAssigned] = useState<AssignedJob[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    totalResults: 0,
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isTriggering, setIsTriggering] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedDateFrom, setSelectedDateFrom] = useState<string>("");
  const [selectedDateTo, setSelectedDateTo] = useState<string>("");

  const { user } = useRbac();

  useEffect(() => {
    fetchAssigned(searchTerm, selectedDateFrom, selectedDateTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, user?.role]);

  const fetchAssigned = async (
    search: string = "",
    fromDate: string = "",
    toDate: string = ""
  ): Promise<void> => {
    try {
      setIsLoading(true);

      // ✅ Convert numeric pagination values to strings
      const query: AssignedJobQuery = {
        search,
        fromDate,
        toDate,
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      };

      const response: ApiResponse[] = await getAssignedJobsForHr(query);

      setAssigned(response[0].data);
      setPagination((prev) => ({
        ...prev,
        totalResults: response[0]?.metadata?.[0]?.totalResults || 0,
      }));
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTriggerCandidateOfTheDay = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to trigger Candidate of the Day assignment now?"
    );
    if (!confirmed) return;

    try {
      setIsTriggering(true);
      await triggerAssignedJobsForAdmin();
      toast.success("Candidate of the Day triggered successfully.");
      await fetchAssigned(searchTerm, selectedDateFrom, selectedDateTo);
    } catch (error) {
      console.error(error);
    } finally {
      setIsTriggering(false);
    }
  };

  return (
    user?.role === "ADMIN" ? (
      <div className="flex flex-col h-screen bg-white border border-gray-200 rounded-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 gap-3 border-b border-gray-200">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900">Assignment History</h1>
            <p className="text-xs sm:text-sm text-gray-500">Candidates of the Day - Admin view</p>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <Input
              placeholder="Search by user/email/mobile/status..."
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setSearchTerm(value);
                fetchAssigned(value, selectedDateFrom, selectedDateTo);
              }}
              className="w-full sm:w-72 border-gray-300"
            />
            <Input
              type="date"
              value={selectedDateFrom}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedDateFrom(value);
                fetchAssigned(searchTerm, value, selectedDateTo);
              }}
              className="w-full sm:w-40 h-10 rounded-lg border-gray-200"
            />
            <Input
              type="date"
              value={selectedDateTo}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedDateTo(value);
                fetchAssigned(searchTerm, selectedDateFrom, value);
              }}
              className="w-full sm:w-40 h-10 rounded-lg border-gray-200"
            />
            <Button
              onClick={handleTriggerCandidateOfTheDay}
              disabled={isTriggering}
              className="bg-[#EC6E51] hover:bg-[#d85f44] text-white"
            >
              {isTriggering ? "Triggering..." : "Trigger Candidate of the Day"}
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-[#FFF5F2] sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mobile</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assignment Time</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assignment Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error Issue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">Loading...</td>
                </tr>
              ) : assigned.length > 0 ? (
                assigned.map((row) => (
                  <tr key={row._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-800">{row.hrName || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.hrRole || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.hrEmail || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.hrMobile || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.createdAt ? new Date(row.createdAt).toLocaleString() : "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.assignmentStatus || row.status || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.errorIssue || "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-500">No assignment history found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 sticky bottom-0 bg-white z-10">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
            disabled={pagination.page === 1}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-gray-600">
            Page {pagination.page} of {Math.max(1, Math.ceil((pagination.totalResults || 0) / pagination.limit))}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setPagination((p) => ({
                ...p,
                page: Math.min(Math.max(1, Math.ceil((pagination.totalResults || 0) / pagination.limit)), p.page + 1),
              }))
            }
            disabled={pagination.page >= Math.max(1, Math.ceil((pagination.totalResults || 0) / pagination.limit))}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    ) : (
      <Table
        headers={["Candidate Name", "Email","Location","Phone", "Applied Date", "Status"]}
        list={assigned}
        setList={setAssigned}
        pagination={pagination}
        setPagination={setPagination}
        user={user}
        onSearch={fetchAssigned}
        isLoading={isLoading}
      />
    )
  );
};

export default AssignedJobs;
